import {
  clearGetAllAssets,
  getAllAssets,
  deleteAsset,
  updateAsset,
  getAssetById,
  addAsset,
  getAllAssetsTypes,
  getAssetTypeById,
  addAssetType,
  updateAssetType,
  deleteAssetType,
  clearGetAllAssetsTypes,
  clearGetAssetSubTypesByAssetTypeId,
  getAssetSubTypesByAssetTypeId,
  getAllParentAssetTypes,
  getMemberComplaints,
  getAdminComplaints,
  addMemberComplaint,
  changeComplaintStatus,
} from "./AssetsActions";
import { createSlice } from "@reduxjs/toolkit";

export interface AssetSubType {
  assetTypeId: number;
  type: string;
  parentTypeId: number | null;
  parentTypeName: string | null;
  isActive: boolean;
  isDeleted: boolean;
  createdBy: number;
  createdAt: string;
  modifiedBy: number | null;
  modifiedAt: string | null;
}

interface InitialState {
  assetsData: any[] | null;
  assetsLoading: boolean;
  assetsError: any | null;

  addAssetLoading: boolean;
  addAssetError: any | null;

  updateAssetLoading: boolean;
  updateAssetError: any | null;

  getAssetByIdLoading: boolean;
  getAssetByIdError: any | null;
  assetByIdData: any | null;

  deleteAssetLoading: boolean;
  deleteAssetError: any | null;

  assetTypesData: AssetSubType[] | null;
  assetTypesLoading: boolean;
  assetTypesError: any | null;

  getAssetTypeByIdLoading: boolean;
  getAssetTypeByIdError: any | null;
  assetTypeByIdData: AssetSubType | null;

  addAssetTypeLoading: boolean;
  addAssetTypeError: any | null;

  updateAssetTypeLoading: boolean;
  updateAssetTypeError: any | null;

  deleteAssetTypeLoading: boolean;
  deleteAssetTypeError: any | null;

  assetSubTypesData: any[] | null;
  assetSubTypesLoading: boolean;
  assetSubTypesError: any | null;

  parentAssetTypesData: any[] | null;
  parentAssetTypesLoading: boolean;
  parentAssetTypesError: any | null;

  memberComplaintsData: any[] | null;
  memberComplaintsLoading: boolean;
  memberComplaintsError: any | null;

  adminComplaintsData: any[] | null;
  adminComplaintsLoading: boolean;
  adminComplaintsError: any | null;

  addMemberComplaintLoading: boolean;
  addMemberComplaintError: any | null;

  changeComplaintStatusLoading: boolean;
  changeComplaintStatusError: any | null;
}

const initialState: InitialState = {
  assetsData: [],
  assetsLoading: false,
  assetsError: null,

  deleteAssetLoading: false,
  deleteAssetError: null,

  addAssetLoading: false,
  addAssetError: null,

  updateAssetLoading: false,
  updateAssetError: null,

  getAssetByIdLoading: false,
  getAssetByIdError: null,
  assetByIdData: null,

  assetTypesData: null,
  assetTypesLoading: false,
  assetTypesError: null,

  getAssetTypeByIdLoading: false,
  getAssetTypeByIdError: null,
  assetTypeByIdData: null,

  addAssetTypeLoading: false,
  addAssetTypeError: null,

  updateAssetTypeLoading: false,
  updateAssetTypeError: null,

  deleteAssetTypeLoading: false,
  deleteAssetTypeError: null,

  assetSubTypesData: null,
  assetSubTypesLoading: false,
  assetSubTypesError: null,

  parentAssetTypesData: null,
  parentAssetTypesLoading: false,
  parentAssetTypesError: null,

  memberComplaintsData: null,
  memberComplaintsLoading: false,
  memberComplaintsError: null,

  adminComplaintsData: null,
  adminComplaintsLoading: false,
  adminComplaintsError: null,

  addMemberComplaintLoading: false,
  addMemberComplaintError: null,

  changeComplaintStatusLoading: false,
  changeComplaintStatusError: null,
};

const assetsSlice = createSlice({
  name: "assets",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllAssets.pending, (state) => {
      state.assetsError = null;
      state.assetsLoading = true;
      state.assetsData = null;
    });
    builder.addCase(getAllAssets.fulfilled, (state, action) => {
      state.assetsLoading = false;
      state.assetsData = action.payload;
    });
    builder.addCase(getAllAssets.rejected, (state, action) => {
      state.assetsLoading = false;
      state.assetsError = action.payload;
      state.assetsData = null;
    });
    builder.addCase(clearGetAllAssets.type, (state) => {
      state.assetsData = null;
      state.assetsLoading = false;
      state.assetsError = null;
    });
    builder.addCase(clearGetAllAssetsTypes.type, (state) => {
      state.assetTypesData = null;
      state.assetTypesLoading = false;
      state.assetTypesError = null;
    });
    builder.addCase(clearGetAssetSubTypesByAssetTypeId.type, (state) => {
      state.assetSubTypesData = null;
      state.assetSubTypesLoading = false;
      state.assetSubTypesError = null;
    });
    builder.addCase(deleteAsset.pending, (state) => {
      state.deleteAssetError = null;
      state.deleteAssetLoading = true;
    });
    builder.addCase(deleteAsset.fulfilled, (state) => {
      state.deleteAssetLoading = false;
      state.deleteAssetError = null;
    });
    builder.addCase(deleteAsset.rejected, (state, action) => {
      state.deleteAssetLoading = false;
      state.deleteAssetError = action.payload;
    });
    builder.addCase(addAsset.pending, (state) => {
      state.addAssetError = null;
      state.addAssetLoading = true;
    });
    builder.addCase(addAsset.fulfilled, (state) => {
      state.addAssetLoading = false;
      state.addAssetError = null;
    });
    builder.addCase(addAsset.rejected, (state, action) => {
      state.addAssetLoading = false;
      state.addAssetError = action.payload;
    });
    builder.addCase(updateAsset.pending, (state) => {
      state.updateAssetError = null;
      state.updateAssetLoading = true;
    });
    builder.addCase(updateAsset.fulfilled, (state) => {
      state.updateAssetLoading = false;
      state.updateAssetError = null;
    });
    builder.addCase(updateAsset.rejected, (state, action) => {
      state.updateAssetLoading = false;
      state.updateAssetError = action.payload;
    });
    builder.addCase(getAssetById.pending, (state) => {
      state.getAssetByIdError = null;
      state.getAssetByIdLoading = true;
      state.assetByIdData = null;
    });
    builder.addCase(getAssetById.fulfilled, (state, action) => {
      state.getAssetByIdLoading = false;
      state.getAssetByIdError = null;
      state.assetByIdData = action.payload;
    });
    builder.addCase(getAssetById.rejected, (state, action) => {
      state.getAssetByIdLoading = false;
      state.getAssetByIdError = action.payload;
      state.assetByIdData = null;
    });
    builder.addCase(getAllAssetsTypes.pending, (state) => {
      state.assetTypesError = null;
      state.assetTypesLoading = true;
      state.assetTypesData = null;
    });
    builder.addCase(getAllAssetsTypes.fulfilled, (state, action) => {
      state.assetTypesLoading = false;
      state.assetTypesData = action.payload;
    });
    builder.addCase(getAllAssetsTypes.rejected, (state, action) => {
      state.assetTypesLoading = false;
      state.assetTypesError = action.payload;
      state.assetTypesData = null;
    });
    builder.addCase(getAssetTypeById.pending, (state) => {
      state.getAssetTypeByIdError = null;
      state.getAssetTypeByIdLoading = true;
      state.assetTypeByIdData = null;
    });
    builder.addCase(getAssetTypeById.fulfilled, (state, action) => {
      state.getAssetTypeByIdLoading = false;
      state.getAssetTypeByIdError = null;
      state.assetTypeByIdData = action.payload;
    });
    builder.addCase(getAssetTypeById.rejected, (state, action) => {
      state.getAssetTypeByIdLoading = false;
      state.getAssetTypeByIdError = action.payload;
      state.assetTypeByIdData = null;
    });
    builder.addCase(addAssetType.pending, (state) => {
      state.addAssetTypeError = null;
      state.addAssetTypeLoading = true;
    });
    builder.addCase(addAssetType.fulfilled, (state) => {
      state.addAssetTypeLoading = false;
      state.addAssetTypeError = null;
    });
    builder.addCase(addAssetType.rejected, (state, action) => {
      state.addAssetTypeLoading = false;
      state.addAssetTypeError = action.payload;
    });
    builder.addCase(updateAssetType.pending, (state) => {
      state.updateAssetTypeError = null;
      state.updateAssetTypeLoading = true;
    });
    builder.addCase(updateAssetType.fulfilled, (state) => {
      state.updateAssetTypeLoading = false;
      state.updateAssetTypeError = null;
    });
    builder.addCase(updateAssetType.rejected, (state, action) => {
      state.updateAssetTypeLoading = false;
      state.updateAssetTypeError = action.payload;
    });
    builder.addCase(deleteAssetType.pending, (state) => {
      state.deleteAssetTypeError = null;
      state.deleteAssetTypeLoading = true;
    });
    builder.addCase(deleteAssetType.fulfilled, (state) => {
      state.deleteAssetTypeLoading = false;
      state.deleteAssetTypeError = null;
    });
    builder.addCase(deleteAssetType.rejected, (state, action) => {
      state.deleteAssetTypeLoading = false;
      state.deleteAssetTypeError = action.payload;
    });
    builder.addCase(getAssetSubTypesByAssetTypeId.pending, (state) => {
      state.assetSubTypesError = null;
      state.assetSubTypesLoading = true;
      state.assetSubTypesData = null;
    });
    builder.addCase(
      getAssetSubTypesByAssetTypeId.fulfilled,
      (state, action) => {
        state.assetSubTypesLoading = false;
        state.assetSubTypesError = null;
        state.assetSubTypesData = action.payload;
      },
    );
    builder.addCase(getAssetSubTypesByAssetTypeId.rejected, (state, action) => {
      state.assetSubTypesLoading = false;
      state.assetSubTypesError = action.payload;
      state.assetSubTypesData = null;
    });
    builder.addCase(getAllParentAssetTypes.pending, (state) => {
      state.parentAssetTypesError = null;
      state.parentAssetTypesLoading = true;
      state.parentAssetTypesData = null;
    });
    builder.addCase(getAllParentAssetTypes.fulfilled, (state, action) => {
      state.parentAssetTypesLoading = false;
      state.parentAssetTypesError = null;
      state.parentAssetTypesData = action.payload;
    });
    builder.addCase(getAllParentAssetTypes.rejected, (state, action) => {
      state.parentAssetTypesLoading = false;
      state.parentAssetTypesError = action.payload;
      state.parentAssetTypesData = null;
    });
    builder.addCase(getMemberComplaints.pending, (state) => {
      state.memberComplaintsError = null;
      state.memberComplaintsLoading = true;
      state.memberComplaintsData = null;
    });
    builder.addCase(getMemberComplaints.fulfilled, (state, action) => {
      state.memberComplaintsLoading = false;
      state.memberComplaintsError = null;
      state.memberComplaintsData = action.payload;
    });
    builder.addCase(getMemberComplaints.rejected, (state, action) => {
      state.memberComplaintsLoading = false;
      state.memberComplaintsError = action.payload;
      state.memberComplaintsData = null;
    });
    builder.addCase(getAdminComplaints.pending, (state) => {
      state.adminComplaintsError = null;
      state.adminComplaintsLoading = true;
      state.adminComplaintsData = null;
    });
    builder.addCase(getAdminComplaints.fulfilled, (state, action) => {
      state.adminComplaintsLoading = false;
      state.adminComplaintsError = null;
      state.adminComplaintsData = action.payload;
    });
    builder.addCase(getAdminComplaints.rejected, (state, action) => {
      state.adminComplaintsLoading = false;
      state.adminComplaintsError = action.payload;
      state.adminComplaintsData = null;
    });
    builder.addCase(addMemberComplaint.pending, (state) => {
      state.addMemberComplaintError = null;
      state.addMemberComplaintLoading = true;
    });
    builder.addCase(addMemberComplaint.fulfilled, (state) => {
      state.addMemberComplaintLoading = false;
      state.addMemberComplaintError = null;
    });
    builder.addCase(addMemberComplaint.rejected, (state, action) => {
      state.addMemberComplaintLoading = false;
      state.addMemberComplaintError = action.payload;
    });
    builder.addCase(changeComplaintStatus.pending, (state) => {
      state.changeComplaintStatusError = null;
      state.changeComplaintStatusLoading = true;
    });
    builder.addCase(changeComplaintStatus.fulfilled, (state) => {
      state.changeComplaintStatusLoading = false;
      state.changeComplaintStatusError = null;
    });
    builder.addCase(changeComplaintStatus.rejected, (state, action) => {
      state.changeComplaintStatusLoading = false;
      state.changeComplaintStatusError = action.payload;
    });
  },
});

const assetsReducer = assetsSlice.reducer;

export default assetsReducer;
